import {
	DocumentType,
	SubmissionWithParticipantsAndValidDecennialCertificate,
} from "@dune/types";
import { Button } from "@dune/ui";
import { FileStatusChip } from "../../../../component/common/files/FileStatusChip";
import { PreviewFileLink } from "../../../../component/common/files/PreviewFileLink";
import { AddDocumentButton } from "../../../../component/constructionSites/submission/AddDocumentButton";
import { useLinkFileToConstructionSiteReception } from "../../../../api/mutations/constructionSiteReception.mutations";

type RequiredDocumentsTypes = DocumentType;

interface ParticipantDocumentsReceptionProps {
	submissionParticipant: SubmissionWithParticipantsAndValidDecennialCertificate["participantToConstructionSiteSubmissions"][number];
	constructionSiteId: number;
	onDocumentClick?: (file: {
		id: string;
		key: string;
		bucket: string;
		mimeType: string;
		name: string;
	}) => void;
	loadingDocumentId?: string | null;
}

const getDocumentLabel = (documentType: RequiredDocumentsTypes): string => {
	const labels: Partial<Record<RequiredDocumentsTypes, string>> = {
		ATTESTATION_DECENNALE: "Attestation Décennale",
		CONVENTION_HONORAIRES: "Convention d'honoraires",
		DEVIS: "Devis",
		FACTURE: "Facture finale",
		PV_RECEPTION: "PV de réception",
		RAPPORT_INITIAL_CONTROLE_TECHNIQUE: "Rapport",
		RAPPORT_FINAL_CONTROLE_TECHNIQUE: "Rapport final",
		RAPPORT_ETUDE_TECHNIQUE: "Rapport",
		RAPPORT_GEOTECHNIQUE: "Rapport",
		LEVEE_RESERVES_PV_RECEPTION: "Levée de réserves",
	};
	return labels[documentType] || documentType.replace(/_/g, " ").toLowerCase();
};

export const ParticipantDocumentsReception = ({
	submissionParticipant,
	constructionSiteId,
	onDocumentClick,
	loadingDocumentId,
}: ParticipantDocumentsReceptionProps) => {
	const requiredDocuments: RequiredDocumentsTypes[] = [];
	const participant = submissionParticipant.participant;
	const isNewParticipant =
		"isNewForReception" in participant ? participant.isNewForReception : true;

	const requiresLiftingReserves =
		"requiresLiftingReserves" in submissionParticipant
			? submissionParticipant.requiresLiftingReserves
			: false;

	const linkReceptionDoc =
		useLinkFileToConstructionSiteReception(constructionSiteId);

	if (submissionParticipant.type === "DESIGNER") {
		requiredDocuments.push("FACTURE");
		if (submissionParticipant.designerType === "CONTROLEUR_TECHNIQUE") {
			requiredDocuments.push("RAPPORT_FINAL_CONTROLE_TECHNIQUE");
		}
		if (isNewParticipant) {
			requiredDocuments.push("ATTESTATION_DECENNALE");
		}
	} else if (submissionParticipant.type === "BUILDER") {
		requiredDocuments.push("FACTURE", "PV_RECEPTION");
		if (isNewParticipant) {
			requiredDocuments.push("ATTESTATION_DECENNALE");
		}
	} else if (submissionParticipant.type === "MATERIAL") {
		requiredDocuments.push("FACTURE");
	}

	if (requiresLiftingReserves) {
		requiredDocuments.push("LEVEE_RESERVES_PV_RECEPTION");
	}

	const getReceptionFilesForType = (documentType: RequiredDocumentsTypes) =>
		participant.filesOnConstructionSiteReception
			?.filter((file) => file.type === documentType)
			.map((file) => file.File) || [];

	const findFilesForType = (documentType: RequiredDocumentsTypes) => {
		const files = [...getReceptionFilesForType(documentType)];

		if (documentType === "ATTESTATION_DECENNALE") {
			files.push(
				...(submissionParticipant.decennialCertificatesOfSubmissionOrValidFromOthers?.map(
					(cert) => cert.file,
				) || []),
			);
		}
		return files;
	};

	return (
		<div className="flex flex-col gap-4">
			{requiredDocuments.map((documentType) => {
				const files = findFilesForType(documentType);
				return (
					<div key={documentType} className="flex flex-col gap-2">
						<p className="text-sm font-medium">
							{getDocumentLabel(documentType)}
						</p>
						<div className="flex flex-col gap-2">
							{files.length > 0
								? files.map((file) => (
										<div
											key={file.id}
											className="flex gap-2 items-center justify-between"
										>
											{onDocumentClick ? (
												<Button
													variant="link"
													type="button"
													size="sm"
													className="text-blue-600 p-0 h-auto font-normal justify-start"
													onClick={() => onDocumentClick(file)}
													disabled={loadingDocumentId === file.id}
												>
													{file.name}
													{loadingDocumentId === file.id && " (Chargement...)"}
												</Button>
											) : (
												<PreviewFileLink file={file} />
											)}
											<FileStatusChip
												humanProcessingStatus={file.humanProcessingStatus}
											/>
										</div>
									))
								: null}
							<div className="flex justify-start">
								<AddDocumentButton
									constructionSiteId={constructionSiteId}
									onUploadSuccess={(fileId) => {
										linkReceptionDoc.mutate({
											fileId,
											type: documentType,
											participantId: participant.id,
										});
									}}
								/>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
};
